import Vue from "vue";
import askAIApi from "../services/askai.api";
import { DRAFT_DELETE, DRAFT_RESET } from "@/store/draftmessage.module";

export const AI_CHECK_AGENT_PERMISSION = "AICheckAgentPermission";
export const AI_AUTO_DRAFT_THREAD = "AIAutoDraftThread"; // --- toggle per-thread auto draft
export const AI_FEEDBACK = "AISendFeedback"; // --- send feedback api
export const AI_GET_DRAFT = "AIGetDraft"; // --- get draft reply message
export const SET_AI_QUEUE_AUTODRAFT = "setAIQueueAutodraft";
export const CLEAR_AI_QUEUE_AUTODRAFT = "clearAIQueueAutodraft";
export const AI_DEBOUNCE_AUTO_DRAFT = "AIDebounceAutoDraft";

export default {
  state: {
    _ai_agent_allow_ask: false,
    _ai_queue_autodraft: {},
    _ai_auto_draft_threads: JSON.parse(
      localStorage.getItem("rw:ask_ai_auto_draft_threads") || "{}"
    ),
  },
  getters: {
    isAgentAllowedToAskAI: (state) => state._ai_agent_allow_ask,
    isAIAutoDraftEnabled: (state) => (threadId) => {
      if (
        threadId &&
        typeof state._ai_auto_draft_threads[threadId] !== "undefined"
      ) {
        return state._ai_auto_draft_threads[threadId];
      }
      return true;
    },
    getAIQueueAutodraft: (state) => state._ai_queue_autodraft,
  },
  actions: {
    async [AI_CHECK_AGENT_PERMISSION]({ commit }) {
      try {
        const response = await askAIApi.checkPermission();
        const isAllowed = response.data.data?.allowed || false;
        commit(AI_CHECK_AGENT_PERMISSION, { isAllowed: isAllowed });
      } catch (error) {
        console.error("Failed to check Ask AI permission:", error);
        commit(AI_CHECK_AGENT_PERMISSION, { isAllowed: false });
      }
    },
    async [AI_FEEDBACK](context, params) {
      return await askAIApi.sendFeedback(params);
    },
    async [AI_GET_DRAFT]({ commit, state, rootGetters }, payload = {}) {
      try {
        if (state._ai_agent_allow_ask) {
          const threadId = payload.threadId || rootGetters.getDialogActive?.id;
          const subThreadId =
            payload.subThreadId || rootGetters.getDialogActive?.subid;

          if (!threadId || !subThreadId) return;

          // --- Cek status enable/disable auto-draft per thread (default ke true)
          const isThreadAutoDraftEnabled =
            typeof state._ai_auto_draft_threads[threadId] !== "undefined"
              ? state._ai_auto_draft_threads[threadId]
              : true;
          if (!isThreadAutoDraftEnabled) return;

          // --- Jika dipanggil dari background debounce (payload.threadId ada) dan thread ini sedang aktif dibuka, jangan jalankan
          const activeDialog = rootGetters.getDialogActive;
          if (
            payload.threadId &&
            activeDialog &&
            activeDialog.id === threadId
          ) {
            console.log(
              `[Ask AI] Auto-draft background ignored because thread ${threadId} is currently opened.`
            );
            return;
          }

          // --- Pastikan objek draf untuk threadId sudah ada di store agar draftUpdate berhasil
          commit("draftAdd", {
            threadid: threadId,
            message: "",
          });

          // --- Cek apakah agent sudah mengetik draf pesan sendiri agar tidak ter-overwrite
          const currentDraft = rootGetters.getDraftById(threadId);
          if (currentDraft && currentDraft.is_edited_by_staff) {
            console.log(
              `[Ask AI] Suggestion fetching for thread ${threadId} ignored because staff edited the draft.`
            );
            return;
          }

          // --- Kunci input textarea dan reset status cancel
          commit("draftUpdate", {
            threadid: threadId,
            input_enable: false,
            ai_cancelled: false,
          });

          try {
            // --- Hit API Suggestion
            const response = await askAIApi.getSuggestion({
              subthreadid: subThreadId,
              is_auto_draft: true,
            });

            // --- Cek jika proses dibatalkan staff sebelum API selesai
            const checkDraft = rootGetters.getDraftById(threadId);
            if (checkDraft && checkDraft.ai_cancelled) {
              console.warn(
                `[Ask AI] AI request for thread ${threadId} was cancelled by user.`
              );
              return;
            }

            const aiContent = response.data?.data?.content || "";
            const id_ask_ai = response.data?.data?.id || null;

            // --- Simpan draf hasil saran AI ke Vuex
            commit("draftUpdate", {
              threadid: threadId,
              id_ask_ai: id_ask_ai,
              message: aiContent,
              is_edited_by_staff: false,
            });
          } finally {
            // --- Buka kembali input textarea
            commit("draftInputToggle", {
              threadid: threadId,
              input_enable: true,
            });
          }
        }
      } catch (error) {
        console.warn(
          "AI Auto Draft Error:",
          error.response?.data?.message || error.message
        );
      }
    },
    [AI_DEBOUNCE_AUTO_DRAFT](
      { commit, dispatch, state, rootGetters },
      { threadId, subThreadId }
    ) {
      if (!state._ai_agent_allow_ask) return;

      // --- Cek status enable/disable auto-draft per thread (default ke true)
      const isThreadAutoDraftEnabled =
        typeof state._ai_auto_draft_threads[threadId] !== "undefined"
          ? state._ai_auto_draft_threads[threadId]
          : true;
      if (!isThreadAutoDraftEnabled) return;

      // --- Jika thread ini sedang aktif dibuka oleh staff, jangan jalankan auto-draft
      const activeDialog = rootGetters.getDialogActive;
      if (activeDialog && activeDialog.id === threadId) {
        return;
      }

      const currentDraft = rootGetters.getDraftById(threadId);
      if (currentDraft && currentDraft.is_edited_by_staff) {
        console.log(
          `[Debounce] Draft has been edited by staff for thread ${threadId}, ignoring.`
        );
        return;
      }

      commit(CLEAR_AI_QUEUE_AUTODRAFT, { threadId });

      const timerId = setTimeout(() => {
        dispatch(AI_GET_DRAFT, { threadId, subThreadId });
        commit(CLEAR_AI_QUEUE_AUTODRAFT, { threadId });
      }, 10000);

      commit(SET_AI_QUEUE_AUTODRAFT, { threadId, timerId });
    },
  },
  mutations: {
    [SET_AI_QUEUE_AUTODRAFT]: (state, { threadId, timerId }) => {
      Vue.set(state._ai_queue_autodraft, threadId, timerId);
    },
    [CLEAR_AI_QUEUE_AUTODRAFT]: (state, { threadId }) => {
      if (state._ai_queue_autodraft[threadId]) {
        clearTimeout(state._ai_queue_autodraft[threadId]);
      }
      Vue.delete(state._ai_queue_autodraft, threadId);
    },
    [AI_CHECK_AGENT_PERMISSION]: (state, { isAllowed }) => {
      state._ai_agent_allow_ask = isAllowed;
    },
    [AI_AUTO_DRAFT_THREAD]: (state, { threadId, val }) => {
      Vue.set(state._ai_auto_draft_threads, threadId, val);
      localStorage.setItem(
        "rw:ask_ai_auto_draft_threads",
        JSON.stringify(state._ai_auto_draft_threads)
      );
    },
    [DRAFT_DELETE]: (state, threadId) => {
      Vue.delete(state._ai_auto_draft_threads, threadId);
      localStorage.setItem(
        "rw:ask_ai_auto_draft_threads",
        JSON.stringify(state._ai_auto_draft_threads)
      );
    },
    [DRAFT_RESET]: (state) => {
      state._ai_auto_draft_threads = {};
      localStorage.removeItem("rw:ask_ai_auto_draft_threads");
    },
  },
};
